<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  automationId: {
    type: [Number, String],
    default: null,
  },
  publishedVersionId: {
    type: [Number, String],
    default: null,
  },
});

const { t } = useI18n();
const store = useStore();

const COMPARABLE_STATUSES = ['draft', 'in_review', 'approved', 'scheduled'];

const versions = ref([]);
const comparison = ref(null);
const isLoadingVersions = ref(false);
const isLoadingComparison = ref(false);
const isPostingComparison = ref(false);
const feedbackState = ref('idle');
const feedbackDetail = ref('');
const sampleEnabled = ref(true);
const sampleLimit = ref(10);
let activeVersionRequestId = 0;
let activeComparisonRequestId = 0;

const newestFirst = records =>
  [...records].sort((first, second) => {
    const firstNumber = Number(first.version_number || first.id || 0);
    const secondNumber = Number(second.version_number || second.id || 0);
    return secondNumber - firstNumber;
  });

const candidateVersion = computed(() =>
  newestFirst(versions.value).find(version =>
    COMPARABLE_STATUSES.includes(version.status)
  )
);

const publishedVersion = computed(() => {
  if (props.publishedVersionId) {
    return versions.value.find(
      version => String(version.id) === String(props.publishedVersionId)
    );
  }

  return newestFirst(versions.value).find(
    version => version.status === 'published'
  );
});

const normalizedSampleLimit = computed(() => {
  const parsed = Number(sampleLimit.value);
  if (!Number.isFinite(parsed)) return 10;
  return Math.min(Math.max(Math.trunc(parsed), 1), 50);
});

const canCompare = computed(
  () =>
    Boolean(props.automationId && candidateVersion.value) &&
    !isLoadingVersions.value &&
    !isLoadingComparison.value &&
    !isPostingComparison.value
);

const diff = computed(() => comparison.value?.diff || {});
const changes = computed(() => diff.value?.changes || []);
const diffSummary = computed(() => diff.value?.summary || {});
const explanation = computed(() => comparison.value?.explanation || {});
const readOnlyContract = computed(
  () => comparison.value?.read_only_contract || {}
);
const shadowMode = computed(() => comparison.value?.shadow_mode || {});
const candidateSimulation = computed(
  () => comparison.value?.simulations?.candidate || null
);
const publishedSimulation = computed(
  () => comparison.value?.simulations?.published || null
);
const totalChanges = computed(
  () => explanation.value.total_changes ?? diffSummary.value.total_changes ?? 0
);
const changedSections = computed(
  () => explanation.value.changed_sections || []
);
const hasChanges = computed(() => changes.value.length > 0);
const hasPublishedBaseline = computed(() =>
  Boolean(comparison.value?.published_version)
);
const readOnlyBadges = computed(() => [
  {
    key: 'writes',
    active: readOnlyContract.value.writes === false,
    label: t('AUTOMATION.COMPARISON.READ_ONLY.NO_WRITES'),
  },
  {
    key: 'effects',
    active: readOnlyContract.value.effect_services_called === false,
    label: t('AUTOMATION.COMPARISON.READ_ONLY.NO_EFFECTS'),
  },
  {
    key: 'redaction',
    active: readOnlyContract.value.raw_action_params_exposed === false,
    label: t('AUTOMATION.COMPARISON.READ_ONLY.REDACTED'),
  },
  {
    key: 'shadow',
    active: shadowMode.value.persisted === false,
    label: t('AUTOMATION.COMPARISON.READ_ONLY.SHADOW_PREVIEW'),
  },
]);

const simulationRows = computed(() => [
  {
    key: 'candidate',
    label: t('AUTOMATION.COMPARISON.SIMULATION.CANDIDATE'),
    version: comparison.value?.candidate_version,
    simulation: candidateSimulation.value,
  },
  {
    key: 'published',
    label: t('AUTOMATION.COMPARISON.SIMULATION.PUBLISHED'),
    version: comparison.value?.published_version,
    simulation: publishedSimulation.value,
  },
]);

const feedbackMessage = computed(() => {
  if (isLoadingVersions.value) return t('AUTOMATION.COMPARISON.LOADING');
  if (isLoadingComparison.value) {
    return t('AUTOMATION.COMPARISON.LOADING_COMPARISON');
  }
  if (isPostingComparison.value) return t('AUTOMATION.COMPARISON.RUNNING');
  if (feedbackState.value === 'load_error') {
    return t('AUTOMATION.COMPARISON.LOAD_ERROR');
  }
  if (feedbackState.value === 'permission') {
    return t('AUTOMATION.COMPARISON.PERMISSION');
  }
  if (feedbackState.value === 'validation') {
    return t('AUTOMATION.COMPARISON.VALIDATION');
  }
  if (feedbackState.value === 'error') return t('AUTOMATION.COMPARISON.ERROR');
  if (feedbackState.value === 'conflict') {
    return t('AUTOMATION.COMPARISON.CONFLICT');
  }
  if (feedbackState.value === 'no_published') {
    return t('AUTOMATION.COMPARISON.NO_PUBLISHED');
  }
  if (!candidateVersion.value) return t('AUTOMATION.COMPARISON.EMPTY');
  if (feedbackState.value === 'success') {
    return t('AUTOMATION.COMPARISON.SUCCESS');
  }
  return t('AUTOMATION.COMPARISON.READY');
});

const feedbackToneClass = computed(() => {
  if (
    ['conflict', 'no_published', 'validation'].includes(feedbackState.value)
  ) {
    return 'bg-n-amber-3 text-n-amber-12';
  }
  if (['load_error', 'error', 'permission'].includes(feedbackState.value)) {
    return 'bg-n-ruby-2 text-n-ruby-11';
  }
  if (feedbackState.value === 'success') {
    return 'bg-n-teal-3 text-n-teal-12';
  }
  return 'bg-n-slate-2 text-n-slate-11';
});

const feedbackRole = computed(() =>
  [
    'conflict',
    'no_published',
    'validation',
    'load_error',
    'error',
    'permission',
  ].includes(feedbackState.value)
    ? 'alert'
    : 'status'
);

function sectionLabel(section) {
  switch (section) {
    case 'event_name':
      return t('AUTOMATION.COMPARISON.SECTION.EVENT');
    case 'conditions':
      return t('AUTOMATION.COMPARISON.SECTION.CONDITIONS');
    case 'actions':
      return t('AUTOMATION.COMPARISON.SECTION.ACTIONS');
    case 'priority':
      return t('AUTOMATION.COMPARISON.SECTION.PRIORITY');
    case 'failure_policy':
      return t('AUTOMATION.COMPARISON.SECTION.FAILURE_POLICY');
    case 'metadata':
      return t('AUTOMATION.COMPARISON.SECTION.METADATA');
    default:
      return t('AUTOMATION.COMPARISON.SECTION.OTHER');
  }
}

function sectionFromPath(path) {
  return path.toString().split('.')[0].split('[')[0];
}

function changeTypeLabel(type) {
  switch (type) {
    case 'added':
      return t('AUTOMATION.COMPARISON.TYPE.ADDED');
    case 'removed':
      return t('AUTOMATION.COMPARISON.TYPE.REMOVED');
    case 'changed':
    default:
      return t('AUTOMATION.COMPARISON.TYPE.CHANGED');
  }
}

function changeTypeClass(type) {
  const classMap = {
    added: 'bg-n-teal-3 text-n-teal-11',
    removed: 'bg-n-ruby-3 text-n-ruby-11',
    changed: 'bg-n-amber-3 text-n-amber-11',
  };
  return classMap[type] || 'bg-n-slate-3 text-n-slate-11';
}

function statusLabel(status) {
  switch (status) {
    case 'draft':
      return t('AUTOMATION.COMPARISON.STATUS.DRAFT');
    case 'in_review':
      return t('AUTOMATION.COMPARISON.STATUS.IN_REVIEW');
    case 'approved':
      return t('AUTOMATION.COMPARISON.STATUS.APPROVED');
    case 'scheduled':
      return t('AUTOMATION.COMPARISON.STATUS.SCHEDULED');
    case 'published':
      return t('AUTOMATION.COMPARISON.STATUS.PUBLISHED');
    case 'paused':
      return t('AUTOMATION.COMPARISON.STATUS.PAUSED');
    case 'superseded':
      return t('AUTOMATION.COMPARISON.STATUS.SUPERSEDED');
    case 'archived':
      return t('AUTOMATION.COMPARISON.STATUS.ARCHIVED');
    default:
      return t('AUTOMATION.COMPARISON.STATUS.UNKNOWN');
  }
}

function versionLabel(version) {
  if (!version) return t('AUTOMATION.COMPARISON.VERSION_EMPTY');
  if (version.version_number) {
    return t('AUTOMATION.COMPARISON.VERSION_LABEL', {
      version: version.version_number,
    });
  }
  return t('AUTOMATION.COMPARISON.VERSION_ID', { id: version.id });
}

function diffStateLabel(state) {
  switch (state) {
    case 'candidate_changes_published':
      return t('AUTOMATION.COMPARISON.DIFF_STATE.CHANGED');
    case 'candidate_matches_published':
      return t('AUTOMATION.COMPARISON.DIFF_STATE.MATCHES');
    case 'no_published_version':
      return t('AUTOMATION.COMPARISON.DIFF_STATE.NO_PUBLISHED');
    default:
      return state || t('AUTOMATION.COMPARISON.UNKNOWN');
  }
}

function simulationStateLabel(state) {
  switch (state) {
    case 'candidate_only':
      return t('AUTOMATION.COMPARISON.SIMULATION_STATE.CANDIDATE_ONLY');
    case 'candidate_invalid':
      return t('AUTOMATION.COMPARISON.SIMULATION_STATE.CANDIDATE_INVALID');
    case 'published_invalid':
      return t('AUTOMATION.COMPARISON.SIMULATION_STATE.PUBLISHED_INVALID');
    case 'same_simulated_outcome':
      return t('AUTOMATION.COMPARISON.SIMULATION_STATE.SAME');
    case 'different_simulated_outcome':
      return t('AUTOMATION.COMPARISON.SIMULATION_STATE.DIFFERENT');
    default:
      return state || t('AUTOMATION.COMPARISON.UNKNOWN');
  }
}

const changedSectionsLabel = computed(() => {
  if (!changedSections.value.length) {
    return t('AUTOMATION.COMPARISON.SUMMARY.NO_SECTIONS');
  }

  return changedSections.value.map(sectionLabel).join(', ');
});

const comparisonSummaryCards = computed(() => [
  {
    key: 'changes',
    label: t('AUTOMATION.COMPARISON.SUMMARY.CHANGES'),
    value: totalChanges.value,
  },
  {
    key: 'sections',
    label: t('AUTOMATION.COMPARISON.SUMMARY.SECTIONS'),
    value: changedSectionsLabel.value,
  },
  {
    key: 'diff',
    label: t('AUTOMATION.COMPARISON.SUMMARY.DIFF'),
    value: diffStateLabel(explanation.value.diff_state),
  },
  {
    key: 'simulation',
    label: t('AUTOMATION.COMPARISON.SUMMARY.SIMULATION'),
    value: simulationStateLabel(explanation.value.simulation_state),
  },
]);

function executionLabel(preview) {
  return preview.would_execute
    ? t('AUTOMATION.COMPARISON.ACTION.WOULD_EXECUTE')
    : t('AUTOMATION.COMPARISON.ACTION.SUPPRESSED');
}

function conditionsLabel(simulation) {
  if (!simulation) return t('AUTOMATION.COMPARISON.SIMULATION.NOT_AVAILABLE');
  return simulation.conditions_matched
    ? t('AUTOMATION.COMPARISON.SIMULATION.CONDITIONS_MATCHED')
    : t('AUTOMATION.COMPARISON.SIMULATION.CONDITIONS_NOT_MATCHED');
}

function impactValue(simulation, key) {
  return simulation?.impact_estimate?.[key] ?? 0;
}

function actionPreviews(simulation) {
  return simulation?.action_previews || [];
}

function formatValue(value) {
  if (value === null || value === undefined || value === '') {
    return t('AUTOMATION.COMPARISON.EMPTY_VALUE');
  }
  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }
  return String(value);
}

function comparisonStateFor(payload) {
  const payloadExplanation = payload?.explanation || {};
  if (payloadExplanation.diff_state === 'no_published_version') {
    return 'no_published';
  }
  if (
    payloadExplanation.diff_state === 'candidate_changes_published' ||
    payloadExplanation.simulation_state === 'different_simulated_outcome'
  ) {
    return 'conflict';
  }
  return 'success';
}

function mapErrorState(error) {
  const status = error?.response?.status;
  feedbackDetail.value = error?.response?.data?.error || '';
  if (status === 409) return 'conflict';
  if (status === 403) return 'permission';
  if (status === 404) return 'load_error';
  if (status === 422) return 'validation';
  return 'error';
}

function comparisonPayload() {
  return {
    sample: {
      enabled: sampleEnabled.value,
      limit: normalizedSampleLimit.value,
    },
  };
}

async function loadComparison({ method = 'get' } = {}) {
  if (!canCompare.value) return;

  const requestId = activeComparisonRequestId + 1;
  activeComparisonRequestId = requestId;
  const isPost = method === 'post';
  comparison.value = null;
  feedbackState.value = 'idle';
  feedbackDetail.value = '';

  if (isPost) {
    isPostingComparison.value = true;
  } else {
    isLoadingComparison.value = true;
  }

  try {
    const action = isPost
      ? 'automations/compareCandidatePublished'
      : 'automations/candidatePublishedComparison';
    const payload = await store.dispatch(action, {
      id: props.automationId,
      versionId: candidateVersion.value.id,
      comparison: comparisonPayload(),
    });

    if (requestId === activeComparisonRequestId) {
      comparison.value = payload;
      feedbackState.value = comparisonStateFor(payload);
    }
  } catch (error) {
    if (requestId === activeComparisonRequestId) {
      feedbackState.value = mapErrorState(error);
    }
  } finally {
    if (requestId === activeComparisonRequestId) {
      isLoadingComparison.value = false;
      isPostingComparison.value = false;
    }
  }
}

async function loadVersions() {
  const automationId = props.automationId;
  const requestId = activeVersionRequestId + 1;
  activeVersionRequestId = requestId;
  activeComparisonRequestId += 1;
  versions.value = [];
  comparison.value = null;
  feedbackState.value = 'idle';
  feedbackDetail.value = '';

  if (!automationId) return;

  isLoadingVersions.value = true;

  try {
    const payload = await store.dispatch('automations/versions', automationId);
    if (requestId === activeVersionRequestId) {
      versions.value = payload?.versions || [];
    }
  } catch (error) {
    if (requestId === activeVersionRequestId) {
      feedbackState.value =
        error?.response?.status === 403 ? 'permission' : 'load_error';
      feedbackDetail.value = error?.response?.data?.error || '';
    }
  } finally {
    if (requestId === activeVersionRequestId) {
      isLoadingVersions.value = false;
    }
  }

  if (requestId === activeVersionRequestId && candidateVersion.value) {
    await loadComparison();
  }
}

watch(() => [props.automationId, props.publishedVersionId], loadVersions, {
  immediate: true,
});
</script>

<template>
  <section
    class="mb-5 rounded-lg border border-n-weak bg-n-background p-3"
    data-test="automation-candidate-comparison-panel"
  >
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div class="min-w-0">
        <h3 class="mb-1 text-sm font-medium text-n-slate-12">
          {{ $t('AUTOMATION.COMPARISON.TITLE') }}
        </h3>
        <p class="m-0 text-xs text-n-slate-11">
          {{ $t('AUTOMATION.COMPARISON.SUBTITLE') }}
        </p>
      </div>
      <Button
        faded
        slate
        sm
        icon="i-lucide-refresh-cw"
        :label="$t('AUTOMATION.COMPARISON.REFRESH')"
        :is-loading="isLoadingVersions || isLoadingComparison"
        data-test="automation-candidate-comparison-refresh"
        @click="loadVersions"
      />
    </div>

    <div class="mt-3 grid gap-2 sm:grid-cols-2">
      <div class="rounded-lg border border-n-weak px-3 py-2">
        <div class="text-xs font-medium text-n-slate-11">
          {{ $t('AUTOMATION.COMPARISON.PUBLISHED') }}
        </div>
        <div
          class="mt-1 text-sm font-medium text-n-slate-12"
          data-test="automation-candidate-comparison-published"
        >
          {{ versionLabel(publishedVersion) }}
        </div>
        <div class="mt-1 text-xs text-n-slate-11">
          {{
            publishedVersion
              ? statusLabel(publishedVersion.status)
              : $t('AUTOMATION.COMPARISON.UNPUBLISHED')
          }}
        </div>
      </div>

      <div class="rounded-lg border border-n-weak px-3 py-2">
        <div class="text-xs font-medium text-n-slate-11">
          {{ $t('AUTOMATION.COMPARISON.CANDIDATE') }}
        </div>
        <div
          class="mt-1 text-sm font-medium text-n-slate-12"
          data-test="automation-candidate-comparison-candidate"
        >
          {{ versionLabel(candidateVersion) }}
        </div>
        <div class="mt-1 text-xs text-n-slate-11">
          {{
            candidateVersion
              ? statusLabel(candidateVersion.status)
              : $t('AUTOMATION.COMPARISON.NO_CANDIDATE')
          }}
        </div>
      </div>
    </div>

    <div class="mt-3 grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
      <div class="grid gap-2 rounded-lg border border-n-weak px-3 py-2">
        <label
          class="m-0 flex min-h-8 items-center gap-2 text-xs text-n-slate-11"
        >
          <input
            v-model="sampleEnabled"
            type="checkbox"
            class="m-0"
            data-test="automation-candidate-comparison-sample-toggle"
          />
          {{ $t('AUTOMATION.COMPARISON.SAMPLE_TOGGLE') }}
        </label>
        <label class="m-0 grid gap-1 text-xs text-n-slate-11">
          {{ $t('AUTOMATION.COMPARISON.SAMPLE_LIMIT') }}
          <input
            v-model.number="sampleLimit"
            type="number"
            min="1"
            max="50"
            class="m-0 h-8 rounded-md border border-n-weak bg-n-alpha-2 px-2 text-sm text-n-slate-12"
            data-test="automation-candidate-comparison-sample-limit"
          />
        </label>
      </div>

      <div class="flex items-end justify-end">
        <Button
          solid
          blue
          sm
          icon="i-lucide-git-compare"
          :label="
            isPostingComparison
              ? $t('AUTOMATION.COMPARISON.RUNNING_BUTTON')
              : $t('AUTOMATION.COMPARISON.RUN')
          "
          :disabled="!canCompare"
          :is-loading="isPostingComparison"
          data-test="automation-candidate-comparison-run"
          @click="loadComparison({ method: 'post' })"
        />
      </div>
    </div>

    <div
      class="mt-3 rounded-md px-3 py-2 text-sm"
      :class="feedbackToneClass"
      :role="feedbackRole"
      data-test="automation-candidate-comparison-feedback"
    >
      {{ feedbackMessage }}
      <span v-if="feedbackDetail" class="block pt-1 text-xs">
        {{ feedbackDetail }}
      </span>
    </div>

    <div v-if="comparison" class="mt-3 grid gap-3">
      <div
        class="flex flex-wrap gap-2"
        data-test="automation-candidate-comparison-read-only"
      >
        <span
          v-for="badge in readOnlyBadges"
          :key="badge.key"
          class="rounded-md px-2 py-1 text-xs font-medium"
          :class="
            badge.active
              ? 'bg-n-teal-3 text-n-teal-12'
              : 'bg-n-amber-3 text-n-amber-12'
          "
        >
          {{ badge.label }}
        </span>
      </div>

      <div
        v-if="!hasPublishedBaseline"
        class="rounded-md bg-n-amber-3 px-3 py-2 text-xs text-n-amber-12"
        data-test="automation-candidate-comparison-no-published"
      >
        {{ $t('AUTOMATION.COMPARISON.BASELINE_EMPTY') }}
      </div>

      <div class="grid gap-2 sm:grid-cols-4">
        <div
          v-for="item in comparisonSummaryCards"
          :key="item.key"
          class="rounded-lg border border-n-weak px-3 py-2"
          :data-test="`automation-candidate-comparison-summary-${item.key}`"
        >
          <div class="text-xs text-n-slate-11">
            {{ item.label }}
          </div>
          <div class="text-sm font-medium text-n-slate-12">
            {{ item.value }}
          </div>
        </div>
      </div>

      <div>
        <h4 class="mb-2 text-xs font-medium text-n-slate-11">
          {{ $t('AUTOMATION.COMPARISON.DIFF_TITLE') }}
        </h4>
        <div
          v-if="!hasChanges"
          class="rounded-md bg-n-slate-2 px-3 py-2 text-sm text-n-slate-11"
          data-test="automation-candidate-comparison-no-changes"
        >
          {{ $t('AUTOMATION.COMPARISON.NO_CHANGES') }}
        </div>
        <ul v-else class="m-0 grid list-none gap-2 p-0">
          <li
            v-for="change in changes"
            :key="`${change.path}-${change.change_type}`"
            class="rounded-lg border border-n-weak p-3"
            data-test="automation-candidate-comparison-change"
          >
            <div class="mb-2 flex flex-wrap items-center gap-2">
              <span
                class="rounded-md px-2 py-1 text-xs font-medium"
                :class="changeTypeClass(change.change_type)"
              >
                {{ changeTypeLabel(change.change_type) }}
              </span>
              <span class="text-xs font-medium text-n-slate-12">
                {{ sectionLabel(sectionFromPath(change.path)) }}
              </span>
              <code class="break-all text-xs text-n-slate-11">
                {{ change.path }}
              </code>
            </div>
            <div class="grid gap-2 sm:grid-cols-2">
              <div>
                <div class="mb-1 text-xs font-medium text-n-slate-11">
                  {{ $t('AUTOMATION.COMPARISON.BEFORE') }}
                </div>
                <div
                  class="m-0 min-h-9 whitespace-pre-wrap break-words rounded-md bg-n-slate-2 p-2 text-xs text-n-slate-12"
                >
                  {{ formatValue(change.before) }}
                </div>
              </div>
              <div>
                <div class="mb-1 text-xs font-medium text-n-slate-11">
                  {{ $t('AUTOMATION.COMPARISON.AFTER') }}
                </div>
                <div
                  class="m-0 min-h-9 whitespace-pre-wrap break-words rounded-md bg-n-slate-2 p-2 text-xs text-n-slate-12"
                >
                  {{ formatValue(change.after) }}
                </div>
              </div>
            </div>
          </li>
        </ul>
      </div>

      <div class="grid gap-3 sm:grid-cols-2">
        <div
          v-for="row in simulationRows"
          :key="row.key"
          class="rounded-lg border border-n-weak p-3"
          :data-test="`automation-candidate-comparison-simulation-${row.key}`"
        >
          <div class="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h4 class="m-0 text-xs font-medium text-n-slate-11">
              {{ row.label }}
            </h4>
            <span
              class="rounded-md bg-n-slate-3 px-2 py-1 text-xs text-n-slate-11"
            >
              {{ versionLabel(row.version) }}
            </span>
          </div>
          <div v-if="row.simulation" class="grid gap-2 text-xs text-n-slate-11">
            <div class="font-medium text-n-slate-12">
              {{ row.simulation.status || $t('AUTOMATION.COMPARISON.UNKNOWN') }}
            </div>
            <div>{{ conditionsLabel(row.simulation) }}</div>
            <div class="grid gap-2 sm:grid-cols-3">
              <div class="rounded-md bg-n-slate-2 px-2 py-1">
                {{ $t('AUTOMATION.COMPARISON.SIMULATION.FANOUT') }}
                {{
                  impactValue(
                    row.simulation,
                    'estimated_matching_conversations'
                  )
                }}
              </div>
              <div class="rounded-md bg-n-slate-2 px-2 py-1">
                {{ $t('AUTOMATION.COMPARISON.SIMULATION.EXECUTIONS') }}
                {{ impactValue(row.simulation, 'estimated_action_executions') }}
              </div>
              <div class="rounded-md bg-n-slate-2 px-2 py-1">
                {{ $t('AUTOMATION.COMPARISON.SIMULATION.COST') }}
                {{ impactValue(row.simulation, 'estimated_cost_units') }}
              </div>
            </div>
            <ul
              v-if="actionPreviews(row.simulation).length"
              class="m-0 grid list-none gap-1 p-0"
            >
              <li
                v-for="preview in actionPreviews(row.simulation)"
                :key="`${row.key}-${preview.index}-${preview.action_name}`"
                class="rounded-md bg-n-slate-2 px-2 py-1"
                data-test="automation-candidate-comparison-action"
              >
                <span class="font-medium text-n-slate-12">
                  {{ preview.action_name }}
                </span>
                <span class="pl-2">
                  {{ executionLabel(preview) }}
                </span>
              </li>
            </ul>
            <div v-else class="rounded-md bg-n-slate-2 px-2 py-1">
              {{ $t('AUTOMATION.COMPARISON.ACTION.EMPTY') }}
            </div>
          </div>
          <div
            v-else
            class="rounded-md bg-n-slate-2 px-3 py-2 text-xs text-n-slate-11"
          >
            {{ $t('AUTOMATION.COMPARISON.SIMULATION.NOT_AVAILABLE') }}
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
